import {
  Button,
  FileInput,
  Modal,
  Select,
  TextInput,
  Textarea,
  Tooltip,
} from 'flowbite-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Loader from '../Common/Loader';
import { useAppDispatch, useAppSelector } from '../../Reducers/store';
import { EquipmentFeature } from '../../Models/EquipmentFeature';
import EquipmentApi from '../../Api/EquipmentApi';
import { formatNumbers, isNumber, onlyNumberInput } from '../../Utils';
import CreatableSelect from 'react-select/creatable';
import { SelectOption } from '../../Models/SelectOption';
import { fetchCustomOptions } from '../../Actions/CommonFields';
import blankImage from '../../Assets/NoPhoto.jpg';
import { CustomOptionsEnum } from '../../Models/CustomOptionsEnum';
import { File as ImageFile } from '../../Models/File';
import { MdCancel } from 'react-icons/md';
import CommonApi from '../../Api/CommonApi';

export default function AddEquipmentFeatureModal({
  show,
  setShow,
  getEquipmentFeatures,
  equipmentId,
  equipmentFeature,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  equipmentId: number;
  getEquipmentFeatures: () => Promise<void> | undefined;
  equipmentFeature: EquipmentFeature | null;
}) {
  const dispatch = useAppDispatch();
  const { customOptions } = useAppSelector((state) => state.commonFields);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>();
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [groupName, setGroupName] = useState<SelectOption | null>();
  const [featureName, setFeatureName] = useState<SelectOption | null>();
  const [imageFile, setImageFile] = useState<ImageFile | null>(null);

  const [formData, setFormData] = useState<EquipmentFeature>({
    id: 0,
    equipmentId: 0,
    groupName: null,
    featureName: null,
    description: null,
    price: null,
    files: [],
  });

  const getReferences = async () => {
    if (!customOptions.length) {
      await Promise.all([dispatch(fetchCustomOptions())]);
    }
  };

  useEffect(() => {
    if (show === true) {
      getReferences();
    }
  }, [show]);

  const groupNameOptionsList = useMemo(() => {
    const list = customOptions
      .filter((x) => x.typeId === CustomOptionsEnum.EquipmentFeatureGroupName)
      .map((t) => ({
        label: t.name,
        value: t.id!,
      }));
    return [...list];
  }, [customOptions]);

  const featureNameOptionsList = useMemo(() => {
    const list = customOptions
      .filter((x) => x.typeId === CustomOptionsEnum.EquipmentFeatureFeatureName)
      .map((t) => ({
        label: t.name,
        value: t.id!,
      }));
    return [...list];
  }, [customOptions]);

  const handleSetInitialValues = () => {
    const values = equipmentFeature
      ? {
          ...equipmentFeature,
          price: equipmentFeature.price
            ? formatNumbers(equipmentFeature.price)
            : '',
        }
      : {
          id: 0,
          equipmentId: equipmentId,
          groupName: null,
          featureName: null,
          description: null,
          price: null,
          files: [],
        };
    const groupNameObj = groupNameOptionsList?.find(
      (option) => option.label === values.groupName
    );
    const featureNameObj = featureNameOptionsList?.find(
      (option) => option.label === values.featureName
    );
    if (groupNameObj) {
      setGroupName({
        label: groupNameObj.label!,
        value: groupNameObj.value!,
      });
    } else {
      setGroupName(null);
    }
    if (featureNameObj) {
      setFeatureName({
        label: featureNameObj.label!,
        value: featureNameObj.value!,
      });
    } else {
      setFeatureName(null);
    }
    setImageFile(
      values.files && values.files.length > 0
        ? (values.files[0] as ImageFile)
        : null
    );
    setFormData(values as EquipmentFeature);
  };

  useEffect(() => {
    handleSetInitialValues();
  }, [equipmentFeature, groupNameOptionsList, featureNameOptionsList]);

  const handleResetFormValues = () => {
    const values = {
      id: 0,
      equipmentId: equipmentId,
      groupName: null,
      featureName: null,
      description: null,
      price: null,
      files: [],
    };
    setGroupName(null);
    setFeatureName(null);
    setFormData(values as EquipmentFeature);
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleChangeCurrencyInput = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    const formattedValue = formatNumbers(value);
    setFormData((prevData) => ({ ...prevData, [name]: formattedValue }));
  };

  function handleImageChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.currentTarget.files) {
      setFile(event.currentTarget.files[0]);
    } else {
      setFile(null);
    }
  }

  function cleanMoneyValues(formData: EquipmentFeature) {
    formData.price = Number((formData.price ?? 0).toString().replace(',', ''));
  }

  const onSubmit = async () => {
    setButtonDisabled(true);
    // e.preventDefault();
    let equipmentUpdated = false;
    let imageUpdated = false;
    try {
      setIsLoading(true);
      cleanMoneyValues(formData);
      formData.groupName = groupName!.label!;
      formData.featureName = featureName!.label!;
      await EquipmentApi.addUpdateEquipmentFeature(formData)
        .then((res) => {
          if (res.data.success) {
            equipmentUpdated = true;
            dispatch(fetchCustomOptions());
          } else {
            toast.error(res.data.message);
          }
        })
        .catch((err) => {
          toast.error('Error: ' + err);
          setIsLoading(false);
        });
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('equipmentFeatureId', formData.id.toString());
        await EquipmentApi.uploadEquipmentFeatureImage(fd)
          .then((res) => {
            if (res.data.success) {
              imageUpdated = true;
            }
          })
          .catch((err) => {
            toast.error('Image Error: ' + err);
          });
      } else {
        imageUpdated = true;
      }
      setIsLoading(false);
      setButtonDisabled(false);
      if (equipmentUpdated && imageUpdated) {
        toast.success('Save Success');
        handleResetFormValues();
        getEquipmentFeatures();
        onCloseModal();
      } else {
        toast.error('Error while saving');
      }
      return;
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setButtonDisabled(false);
    }
  };

  function handleRemoveImage(id: number) {
    const shouldRemove = window.confirm(
      'Are you sure you want to delete this image?'
    );

    if (shouldRemove) {
      CommonApi.deleteFile(id)
        .then((res) => {
          if (res.data.success) {
            setImageFile(null);
            getEquipmentFeatures();
            toast.success('Image deleted');
          } else {
            toast.success('Error while deleting image');
          }
        })
        .catch((err) => {
          toast.error('Image Error: ' + err);
        });
    }
  }

  function onCloseModal() {
    setShow(false);
  }

  return (
    <Modal show={show} size='4xl' onClose={onCloseModal} popup>
      <Modal.Header></Modal.Header>
      <Modal.Body>
        <div className='w-full'>
          <div className='w-full'>
            <div className='mx-1 flex items-end justify-center'>
              <div className='flex flex-1 justify-center'>
                <h1 className='text-gray-900 font-bold text-4xl'>
                  Add Feature
                </h1>
              </div>
            </div>
          </div>

          <div>
            <div className='container mx-auto p-2'>
              <div className='md:flex no-wrap md:-mx-2 '>
                <div className='w-full md:w-3/12 md:mx-2'>
                  <div className='h-56 border-2 rounded-lg relative'>
                    <div>
                      {imageFile && (
                        <div className='w-full absolute top-2 flex justify-center gap-3'>
                          <Tooltip content='Delete Image'>
                            <button
                              type='submit'
                              title='Delete Image'
                              onClick={() => handleRemoveImage(imageFile.id)}
                              className='rounded-full bg-white'
                            >
                              <MdCancel color='red' size={20} />
                            </button>
                          </Tooltip>
                        </div>
                      )}
                      <img
                        src={imageFile ? imageFile.fileUrl : blankImage}
                        alt={formData.id + '-image'}
                        className='h-56 object-contain m-auto'
                      />
                    </div>
                  </div>
                  <div>
                    <FileInput
                      id='file-upload'
                      helperText='PNG or JPG.'
                      multiple
                      onChange={handleImageChange}
                      disabled={buttonDisabled}
                    />
                  </div>
                </div>
                <div className='w-full'>
                  <div className='bg-white'>
                    <div className='flex items-center space-x-2 font-bold text-gray-500 leading-8'>
                      <span className='tracking-wide uppercase text-3xl'>
                        Information
                      </span>
                    </div>
                    <hr className='bg-gray-300 h-[5px]'></hr>
                    <form>
                      <div className='text-gray-500'>
                        <div className='grid grid-cols-[20%_80%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Group:
                            </div>
                          </div>
                          <div className='w-full'>
                            <CreatableSelect
                              name='groupName'
                              placeholder='Select previous or add new...'
                              isClearable
                              value={groupName}
                              onChange={(e) => {
                                if (e) {
                                  setGroupName(e);
                                } else {
                                  setGroupName(null);
                                }
                              }}
                              options={groupNameOptionsList}
                              required
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[20%_80%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Feature:
                            </div>
                          </div>
                          <div className='w-full'>
                            <CreatableSelect
                              name='featureName'
                              placeholder='Select previous or add new...'
                              isClearable
                              value={featureName}
                              onChange={(e) => {
                                if (e) {
                                  setFeatureName(e);
                                } else {
                                  setFeatureName(null);
                                }
                              }}
                              options={featureNameOptionsList}
                              required
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[20%_80%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Description:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='description'
                              name='description'
                              type='text'
                              placeholder='Description...'
                              onChange={handleChangeInput}
                              value={formData.description ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[20%_80%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Price:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='price'
                              name='price'
                              type='text'
                              placeholder='0.00'
                              onChange={handleChangeCurrencyInput}
                              onKeyDown={(event) => {
                                if (onlyNumberInput(event)) {
                                  event.preventDefault();
                                }
                              }}
                              value={formData.price ?? ''}
                              sizing='md'
                              addon='$'
                            />
                          </div>
                        </div>
                      </div>
                      <div className='flex justify-end pt-2'>
                        <Button
                          disabled={buttonDisabled}
                          type='button'
                          className='buttonMain'
                          onClick={() => onSubmit()}
                        >
                          Save
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
